<template>
  <main class="mypage-redesign">
    <!-- 메인 콘텐츠 영역 -->
    <div class="mypage-container">
      <!-- MypageSidebar 컴포넌트 -->
      <MypageSidebar />

      <!-- 메인 콘텐츠 -->
      <div class="main-content">
        <!-- 주문/결제 내역 섹션 -->
        <section class="order-section">
          <div class="section-header">
            <div>
              <h2 class="section-title">
                {{ $route.path === '/mypage/paymentList' ? '결제 내역' : '주문내역' }}
              </h2>
              <p class="section-description">
                {{ $route.path === '/mypage/paymentList' ? '결제 내역을 확인하실 수 있습니다' : '주문 내역을 확인하실 수 있습니다' }}
              </p>
            </div>
          </div>

          <!-- 서브 탭 네비게이션 -->
          <div class="sub-tabs">
            <a href="/profile/confList" target="_blank" class="sub-tab">
              컨퍼런스 신청내역
              <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="external-icon" />
            </a>
            <router-link to="/mypage/list" class="sub-tab">주문내역</router-link>
            <router-link to="/mypage/paymentList" class="sub-tab">결제 내역</router-link>
          </div>

          <!-- 날짜 필터 -->
          <div class="filter-section">
            <div class="filter-row">
              <label class="filter-label">기간조회</label>
              <VDatePicker v-model.range="range" locale="ko" :max-date="new Date()">
                <template #default="{ inputValue, inputEvents }">
                  <div class="date-picker-wrapper">
                    <input type="text" class="date-input" :value="inputValue.start" v-on="inputEvents.start" />
                    <span class="date-separator">~</span>
                    <input type="text" class="date-input" :value="inputValue.end" v-on="inputEvents.end" />
                    <button class="search-button" type="button" @click="getMyOrderList(1)">조회</button>
                  </div>
                </template>
              </VDatePicker>
            </div>
          </div>

          <!-- 주문내역 테이블 -->
          <div v-if="$route.path === '/mypage/list' && myOrderList.length > 0" class="order-table-section">
            <div class="order-table">
              <!-- 데스크톱 헤더 -->
              <div class="order-table-header">
                <div class="order-col-info">주문정보</div>
                <div class="order-col-payment">결제정보</div>
                <div class="order-col-detail">주문상세</div>
                <div class="order-col-receipt">영수증</div>
              </div>

              <!-- 데이터 행 -->
              <div v-for="item in myOrderList" :key="item.oid" class="order-table-row">
                <!-- 주문정보 -->
                <div class="order-col-info order-info-cell">
                  <p class="order-id">{{ item.oid }}</p>
                  <h3 class="order-name">{{ item.goodname }}</h3>
                  <p class="order-date">{{ formDate(item.confirm_date) }}</p>
                </div>

                <!-- 결제정보 -->
                <div class="order-col-payment order-payment-cell">
                  <p class="payment-amount">
                    <span class="amount">{{ item.totalPaidAmount.toLocaleString() }}</span>원
                  </p>
                  <p v-if="item.totalPaidAmount > 0" class="payment-method">
                    {{
                      item.gopaymethod === 'Card' ? '카드결제' :
                      item.gopaymethod === 'VCard' ? 'ISP 결제' :
                      item.gopaymethod === 'DirectBank' ? '실시간 계좌이체' :
                      item.gopaymethod === 'VBank' ? '가상계좌(무통장입금)' : ''
                    }}
                    <span
                      class="payment-status"
                      :class="{
                        'status-fail': item.orderStatus === 'PAY_FAIL',
                        'status-done': item.orderStatus === 'ORDER_DONE'
                      }"
                    >
                      [{{
                        item.orderStatus === 'PAY_WAIT' ? '결제 대기' :
                        item.orderStatus === 'VBANK_WAIT' ? '가상계좌 입금 대기' :
                        item.orderStatus === 'PAY_FAIL' ? '결제 실패' :
                        item.orderStatus === 'ORDER_DONE' ? '결제 완료' :
                        item.orderStatus === 'REFUNDED' ? '환불 완료' : ''
                      }}]
                    </span>
                  </p>
                </div>

                <!-- 주문상세 -->
                <div class="order-col-detail order-action-cell">
                  <router-link
                    v-if="item.totalPaidAmount > 0"
                    :to="`/mypage/detail/${item.oid}`"
                    class="action-link"
                  >
                    상세보기
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.74999 15C6.74952 14.9015 6.76874 14.8038 6.80652 14.7128C6.8443 14.6218 6.89988 14.5392 6.96999 14.47L11.44 9.99999L6.99999 5.52999C6.9086 5.38904 6.86718 5.22148 6.88237 5.05418C6.89755 4.88687 6.96847 4.72951 7.08375 4.60731C7.19903 4.48512 7.35199 4.40516 7.51813 4.38026C7.68427 4.35536 7.85395 4.38696 7.99999 4.46999L13 9.47C13.1404 9.61062 13.2193 9.80124 13.2193 9.99999C13.2193 10.1987 13.1404 10.3894 13 10.53L7.99999 15.53C7.85936 15.6704 7.66874 15.7493 7.46999 15.7493C7.27124 15.7493 7.08061 15.6704 6.93999 15.53C6.8093 15.3853 6.74101 15.1948 6.74999 15Z" fill="currentColor"/>
                    </svg>
                  </router-link>
                </div>

                <!-- 영수증 -->
                <div class="order-col-receipt order-action-cell">
                  <a
                    v-if="item.totalPaidAmount > 0"
                    :href="`https://iniweb.inicis.com/receipt/iniReceipt.jsp?noTid=${item.tid}`"
                    target="_blank"
                    class="action-link"
                  >
                    <span class="mobile-label">영수증 출력</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.70001 5.50001C3.70001 5.02262 3.88965 4.56479 4.22722 4.22722C4.56479 3.88965 5.02262 3.70001 5.50001 3.70001H11.8C12.2774 3.70001 12.7352 3.88965 13.0728 4.22722C13.4104 4.56479 13.6 5.02262 13.6 5.50001V11.8H16.3V13.6C16.3 14.3161 16.0155 15.0029 15.5092 15.5092C15.0029 16.0155 14.3161 16.3 13.6 16.3H6.40001C5.68393 16.3 4.99717 16.0155 4.49082 15.5092C3.98448 15.0029 3.70001 14.3161 3.70001 13.6V5.50001ZM13.6 12.7V15.4C14.0774 15.4 14.5352 15.2104 14.8728 14.8728C15.2104 14.5352 15.4 14.0774 15.4 13.6V12.7H13.6ZM12.7 15.4V5.50001C12.7 5.26132 12.6052 5.0324 12.4364 4.86362C12.2676 4.69483 12.0387 4.60001 11.8 4.60001H5.50001C5.26132 4.60001 5.0324 4.69483 4.86362 4.86362C4.69483 5.0324 4.60001 5.26132 4.60001 5.50001V13.6C4.60001 14.0774 4.78965 14.5352 5.12722 14.8728C5.46479 15.2104 5.92262 15.4 6.40001 15.4H12.7ZM6.40001 6.85001C6.40001 6.73066 6.44742 6.61621 6.53181 6.53181C6.61621 6.44742 6.73066 6.40001 6.85001 6.40001H10.45C10.5694 6.40001 10.6838 6.44742 10.7682 6.53181C10.8526 6.61621 10.9 6.73066 10.9 6.85001C10.9 6.96936 10.8526 7.08382 10.7682 7.16821C10.6838 7.2526 10.5694 7.30001 10.45 7.30001H6.85001C6.73066 7.30001 6.61621 7.2526 6.53181 7.16821C6.44742 7.08382 6.40001 6.96936 6.40001 6.85001ZM6.40001 9.55001C6.40001 9.43067 6.44742 9.31621 6.53181 9.23181C6.61621 9.14742 6.73066 9.10001 6.85001 9.10001H10.45C10.5694 9.10001 10.6838 9.14742 10.7682 9.23181C10.8526 9.31621 10.9 9.43067 10.9 9.55001C10.9 9.66936 10.8526 9.78382 10.7682 9.86821C10.6838 9.9526 10.5694 10 10.45 10H6.85001C6.73066 10 6.61621 9.9526 6.53181 9.86821C6.44742 9.78382 6.40001 9.66936 6.40001 9.55001ZM6.40001 12.25C6.40001 12.1307 6.44742 12.0162 6.53181 11.9318C6.61621 11.8474 6.73066 11.8 6.85001 11.8H8.65001C8.76936 11.8 8.88382 11.8474 8.96821 11.9318C9.0526 12.0162 9.10001 12.1307 9.10001 12.25C9.10001 12.3694 9.0526 12.4838 8.96821 12.5682C8.88382 12.6526 8.76936 12.7 8.65001 12.7H6.85001C6.73066 12.7 6.61621 12.6526 6.53181 12.5682C6.44742 12.4838 6.40001 12.3694 6.40001 12.25Z" fill="currentColor"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <!-- 페이지네이션 -->
            <pagination v-if="pageInfo.page" :pageInfo="pageInfo" :page="page" :size="size" @updatePage="updatePage" />
          </div>

          <!-- 결제 내역 테이블 -->
          <div v-if="$route.path === '/mypage/paymentList' && myOrderList.length > 0" class="payment-table-section">
            <div class="payment-table">
              <!-- 데스크톱 헤더 -->
              <div class="payment-table-header">
                <div class="payment-col-tid">거래번호</div>
                <div class="payment-col-date">결제일시</div>
                <div class="payment-col-amount">결제금액</div>
                <div class="payment-col-method">결제수단</div>
                <div class="payment-col-status">상태</div>
                <div class="payment-col-device">결제기기</div>
              </div>

              <!-- 데이터 행 -->
              <div v-for="item in myOrderList" :key="item.oid" class="payment-table-row">
                <div class="payment-col-tid">
                  <span class="mobile-label">거래번호</span>
                  <span class="payment-value">{{ item.tid }}</span>
                </div>
                <div class="payment-col-date">
                  <span class="mobile-label">결제일시</span>
                  <span class="payment-value">{{ formDate(item.confirm_date) }}</span>
                </div>
                <div class="payment-col-amount">
                  <span class="mobile-label">결제금액</span>
                  <span class="payment-value">{{ item.totalPaidAmount.toLocaleString() }}원</span>
                </div>
                <div class="payment-col-method">
                  <span class="mobile-label">결제수단</span>
                  <span class="payment-value">{{ item.gopaymethod }}</span>
                </div>
                <div class="payment-col-status">
                  <span class="mobile-label">상태</span>
                  <span class="payment-value">
                    {{
                      item.orderStatus === 'PAY_WAIT' ? '결제 대기' :
                      item.orderStatus === 'VBANK_WAIT' ? '가상계좌 입금 대기' :
                      item.orderStatus === 'PAY_FAIL' ? '결제 실패' :
                      item.orderStatus === 'ORDER_DONE' ? '결제 완료' :
                      item.orderStatus === 'REFUNDED' ? '환불 완료' : ''
                    }}
                  </span>
                </div>
                <div class="payment-col-device">
                  <span class="mobile-label">결제기기</span>
                  <span class="payment-value">{{ item.pay_device }}</span>
                </div>
              </div>
            </div>

            <!-- 페이지네이션 -->
            <pagination v-if="pageInfo.page" :pageInfo="pageInfo" :page="page" :size="size" @updatePage="updatePage" />
          </div>

          <!-- 빈 상태 -->
          <div v-if="myOrderList.length === 0" class="empty-state">
            {{ $route.path === '/mypage/paymentList' ? '결제 내역이 없습니다.' : '주문 내역이 없습니다.' }}
          </div>
        </section>
      </div>
    </div>
  </main>
</template>

<script setup>
import axios from "axios";
import { onMounted, ref, computed } from "vue";
import MypageSidebar from "@/components/MypageSidebar.vue";
import Pagination from "@/components/Pagination.vue";
import { formDate } from "@/assets/js/util.js";

const formatDate = (date) => date.toISOString().split("T")[0];

const start = new Date();
const end = new Date();
start.setDate(end.getDate() - 30);

const range = ref({ start, end });

const formStartDate = computed(() => formatDate(range.value.start));
const formEndDate = computed(() => formatDate(range.value.end));

const myOrderList = ref([]);

const getMyOrderList = async (num) => {
  const pageValue = num !== undefined ? 1 : page.value;

  try {
    const url = `/new_api/customer/order/v2/list?page=${pageValue}&size=${size.value}&startDate=${formStartDate.value}&endDate=${formEndDate.value}`;
    const res = await axios.get(url);

    myOrderList.value = res.data.data.customerOrderList;
    pageInfo.value = res.data.data.pageInfo;
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  await getMyOrderList();
});

const pageInfo = ref({});

const page = ref(1);
const size = ref(10);

const updatePage = (num) => {
  page.value = num;
  getMyOrderList();
};
</script>

<style scoped lang="scss">
/* ========================================
   Variables & Reset
   ======================================== */
.mypage-redesign {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 0;
  font-family: "Pretendard", "Malgun Gothic", sans-serif;
}

/* ========================================
   Main Container
   ======================================== */
.mypage-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  display: grid;
  grid-template-columns: 230px 1fr;
  gap: 40px;

  @media (max-width: 1023px) {
    grid-template-columns: 240px 1fr;
    gap: 32px;
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    padding: 20px 16px 40px;
  }
}

/* ========================================
   Main Content
   ======================================== */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* ========================================
   Order Section
   ======================================== */
.order-section {
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 767px) {
    padding: 24px 16px;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #222;
  margin: 0 0 8px 0;

  @media (max-width: 767px) {
    font-size: 20px;
  }
}

.section-description {
  font-size: 14px;
  color: #888;
  margin: 0;
}

/* ========================================
   Sub Tabs
   ======================================== */
.sub-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  border-bottom: 1px solid #dee0e3;
  padding-bottom: 0;
  overflow-x: auto;

  @media (max-width: 767px) {
    gap: 4px;
  }
}

.sub-tab {
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #555961;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: #0c3388;
  }

  &.router-link-active {
    color: #0c3388;
    border-bottom-color: #0c3388;
  }

  .external-icon {
    margin-left: 4px;
    font-size: 12px;
  }

  @media (max-width: 767px) {
    padding: 10px 16px;
    font-size: 13px;
  }
}

/* ========================================
   Filter Section
   ======================================== */
.filter-section {
  background: #f5f7fa;
  border: 1px solid #dee0e3;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    padding: 16px;
  }
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  min-width: 80px;

  @media (max-width: 767px) {
    min-width: auto;
  }
}

.date-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;

  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
}

.date-input {
  flex: 1;
  min-width: 140px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 8px;
  font-size: 14px;
  color: #555961;
  transition: all 0.2s;

  &:hover {
    border-color: #0c3388;
  }

  &:focus {
    outline: none;
    border-color: #0c3388;
    box-shadow: 0 0 0 3px rgba(12, 51, 136, 0.1);
  }

  @media (max-width: 767px) {
    min-width: 0;
  }
}

.date-separator {
  font-size: 14px;
  color: #555961;
  flex-shrink: 0;

  @media (max-width: 767px) {
    display: none;
  }
}

.search-button {
  padding: 12px 24px;
  background: #0c3388;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #092968;
  }

  &:focus-visible {
    outline: 2px solid #0c3388;
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
}

/* ========================================
   Order Table
   ======================================== */
.order-table-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.order-table {
  border: 1px solid #dee0e3;
  border-radius: 12px;
  overflow: hidden;
}

.order-table-header {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr;
  background: #f5f7fa;
  padding: 16px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #222;
  border-bottom: 1px solid #dee0e3;

  @media (max-width: 767px) {
    display: none;
  }
}

.order-table-row {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr;
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 20px 16px;
  }
}

.order-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .order-id {
    font-size: 14px;
    font-weight: 600;
    color: #0c3388;
    margin: 0;
  }

  .order-name {
    font-size: 16px;
    font-weight: 600;
    color: #222;
    margin: 0;
    line-height: 1.5;
  }

  .order-date {
    font-size: 14px;
    color: #888;
    margin: 0;
  }
}

.order-payment-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .payment-amount {
    font-size: 18px;
    font-weight: 700;
    color: #222;
    margin: 0;

    .amount {
      color: #0c3388;
    }
  }

  .payment-method {
    font-size: 14px;
    color: #555961;
    margin: 0;
  }

  .payment-status {
    font-weight: 600;

    &.status-fail {
      color: #ef4444;
    }

    &.status-done {
      color: #888;
    }
  }
}

.order-action-cell {
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    justify-content: flex-start;
  }
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #222;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    border-color: #0c3388;
    color: #0c3388;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  .mobile-label {
    @media (min-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 767px) {
    width: 100%;
    justify-content: center;
  }
}

/* ========================================
   Payment Table
   ======================================== */
.payment-table-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.payment-table {
  border: 1px solid #dee0e3;
  border-radius: 12px;
  overflow: hidden;
}

.payment-table-header {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr 1fr;
  background: #f5f7fa;
  padding: 16px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #222;
  border-bottom: 1px solid #dee0e3;

  @media (max-width: 1023px) {
    display: none;
  }
}

.payment-table-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr 1fr;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  transition: all 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  @media (max-width: 1023px) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 20px 16px;
  }

  > div {
    font-size: 14px;
    color: #555961;

    @media (max-width: 1023px) {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }

  .mobile-label {
    display: none;
    font-size: 13px;
    font-weight: 600;
    color: #888;

    @media (max-width: 1023px) {
      display: block;
    }
  }

  .payment-value {
    font-size: 14px;
    color: #222;
    font-weight: 500;
  }
}

/* ========================================
   Empty State
   ======================================== */
.empty-state {
  padding: 60px 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #dee0e3;
}

/* ========================================
   Focus Styles
   ======================================== */
*:focus-visible {
  outline: 2px solid #0c3388;
  outline-offset: 2px;
}
</style>
